import {
  KERNING_MAX,
  KERNING_MIN,
  LEADING_MAX,
  LEADING_METRIC_SENTINEL,
  LEADING_MIN,
  SPACING_PRESET_LABELS,
  TRACKING_MAX,
  TRACKING_MIN,
} from '../constants/typography-ranges'
import type { KerningField } from '../hooks/use-kerning-field'
import type { LeadingField } from '../hooks/use-leading-field'
import type { TrackingField } from '../hooks/use-tracking-field'
import { SectionHeading } from './section-heading'
import { SpacingField } from './spacing-field'

const LEADING_CHIP_OPTIONS = ['Narrow', LEADING_METRIC_SENTINEL, 'Wide'] as const

interface SpacingSectionProps {
  leading: LeadingField
  kerning: KerningField
  tracking: TrackingField
  hasTextSelectionTarget: boolean
}

/** Spacing: Leading, Kerning and Tracking. Renders siblings (a Fragment) so each stays a direct
 *  child of the panel's spaced column. */
export function SpacingSection({
  leading,
  kerning,
  tracking,
  hasTextSelectionTarget,
}: SpacingSectionProps) {
  return (
    <>
      {/* Spacing Section */}
      <div>
        <SectionHeading>Spacing</SectionHeading>
      </div>

      {/* Leading Section — disabled when no compatible text object is selected.
          Its slider row is keepalive-marked (like the Font Size input) so dragging the slider
          re-derives the run ranges/highlight from the saved pre-drag selection on every tick
          instead of fighting the contenteditable for native focus/selection each time, which
          is what produced a stale selection highlight double-painted under the live one. */}
      <SpacingField
        label="Leading"
        labelId="leading-section-label"
        disabled={leading.leadingControlsDisabled}
        blockPointerWhenDisabled
        chipOptions={LEADING_CHIP_OPTIONS}
        chipValue={leading.leadingChip ?? ''}
        onChipChange={leading.handleChipChange}
        sliderValue={leading.sliderValue}
        sliderMin={LEADING_MIN}
        sliderMax={LEADING_MAX}
        sliderStep={0.5}
        onSliderKeyDown={leading.sliderGuard.onKeyDown}
        onSliderChange={leading.handleSliderChange}
        onSliderCommit={leading.sliderGuard.onValueCommit}
        thumbLabel="Leading"
        thumbValueText={leading.sliderValueText}
        sliderRowKeepalive
        inputValue={leading.leadingInputValue}
        mixed={leading.leadingMixed}
        onInputChange={leading.handleInputChange}
        onInputKeyDown={leading.handleInputKeyDown}
        onInputFocus={leading.handleInputFocus}
        onInputBlur={leading.handleInputBlur}
        error={leading.leadingError}
        errorMessage={leading.leadingErrorMsg}
        errorId="leading-error"
        suffix={hasTextSelectionTarget ? 'pt' : undefined}
      />

      {/* Kerning Section — only available with a collapsed caret strictly between two
          characters (Text Tool edit mode); disabled for any selection or in Select Tool.
          Its input, like Tracking's, takes up to 3 whole digits and 2 decimals ("-200.55") —
          enforced in the hook's change handler (`exceedsSpacingInputLimit`). */}
      <SpacingField
        label="Kerning"
        labelId="kerning-section-label"
        disabled={kerning.kerningControlsDisabled}
        blockPointerWhenDisabled
        chipOptions={SPACING_PRESET_LABELS}
        chipValue={kerning.chipValue}
        onChipChange={kerning.handleChipChange}
        sliderValue={kerning.kerning}
        sliderMin={KERNING_MIN}
        sliderMax={KERNING_MAX}
        sliderStep={1}
        onSliderKeyDown={kerning.sliderGuard.onKeyDown}
        onSliderChange={kerning.handleSliderChange}
        onSliderCommit={kerning.sliderGuard.onValueCommit}
        thumbLabel="Kerning"
        thumbValueText={kerning.sliderValueText}
        inputValue={kerning.kerningInputValue}
        inputMode="decimal"
        mixed={kerning.kerningMixed}
        onInputChange={kerning.handleInputChange}
        onInputKeyDown={kerning.handleInputKeyDown}
        onInputFocus={kerning.handleInputFocus}
        onInputBlur={kerning.handleInputBlur}
        error={kerning.kerningError}
        errorMessage={kerning.kerningErrorMsg}
        errorId="kerning-error"
        suffix="em"
      />

      {/* Tracking Section — disabled when no compatible text object is selected. Uses the
          controls' own `disabled` prop (like Radix elsewhere in this panel) rather than an
          ancestor pointer-events/opacity wrapper, so a CSS re-render on this container can never
          interrupt an in-progress Slider drag on one of its descendants. */}
      <SpacingField
        label="Tracking"
        labelId="tracking-section-label"
        disabled={tracking.trackingControlsDisabled}
        blockPointerWhenDisabled={false}
        chipOptions={SPACING_PRESET_LABELS}
        chipValue={tracking.chipValue}
        onChipChange={tracking.handleChipChange}
        sliderValue={tracking.tracking}
        sliderMin={TRACKING_MIN}
        sliderMax={TRACKING_MAX}
        sliderStep={1}
        onSliderKeyDown={tracking.sliderGuard.onKeyDown}
        onSliderChange={tracking.handleSliderChange}
        onSliderCommit={tracking.sliderGuard.onValueCommit}
        thumbLabel="Tracking"
        thumbValueText={tracking.sliderValueText}
        inputValue={tracking.trackingInputValue}
        mixed={tracking.trackingMixed}
        onInputChange={tracking.handleInputChange}
        onInputKeyDown={tracking.handleInputKeyDown}
        onInputFocus={tracking.handleInputFocus}
        onInputBlur={tracking.handleInputBlur}
        error={tracking.trackingError}
        errorMessage={tracking.trackingErrorMsg}
        errorId="tracking-error"
        suffix="em"
      />
    </>
  )
}
